import { BorderedBox, CCTSwitch, ContentBox, Heading2, SwitchFormControlLabel } from './ClientConfigToolStyles'
import React, { ChangeEvent, FormEvent, useState } from 'react'
import clientConfigState, { ClientData, Package } from '../atoms/clientConfigState'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import ClientAuditHistory from './ClientAuditHistory'
import ClientsService from '../service/clients'
import { Grid } from '@mui/material'
import PackageAndFeatures from './PackageAndFeatures'
import ViewMigratedDataConfirmDialog from './ViewMigratedDataConfirmDialog'

type ClientSettingsProps = {
  client: ClientData
  packagesArray: Package[]
  setBackdrop: (value: boolean) => void
}

function ClientSettings({ client, packagesArray, setBackdrop } : ClientSettingsProps) {
  const updateClientState = useSetRecoilState(clientConfigState)
  const clientState = useRecoilValue(clientConfigState)
  const [showViewMigratedDataDialog, setShowViewMigratedDataDialog] = useState(false)
  const [submittingViewMigratedDataDialog, setSubmittingViewMigratedDataDialog] = useState(false)
  const [pendingViewMigratedDataValue, setPendingViewMigratedDataValue] = useState(false)
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateClientState({
      ...clientState,
      data: {
        ...clientState.data,
        [event.target.name]: event.target.checked,
      },
    })
  }

  const handleViewMigratedDataChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPendingViewMigratedDataValue(event.target.checked)
    setShowViewMigratedDataDialog(true)
  }

  const handleViewMigratedDataConfirm = (event: FormEvent) => {
    event.preventDefault()
    setSubmittingViewMigratedDataDialog(true)
    const backdropTimeout = setTimeout(() => {
      setBackdrop(true)
    }, 3000)

    const request = ClientsService.updateViewMigratedDataFlag(client.client_id, pendingViewMigratedDataValue)
    request.then(() => {
      updateClientState({
        ...clientState,
        config: {
          ...clientState.config,
          alertMessage: 'Your recent changes have been saved successfully',
          alertSeverity: 'success',
          alertTitle: 'Success',
          isUpdate: true,
          showAlert: true,
        },
        data: {
          ...clientState.data,
          viewMigratedData: pendingViewMigratedDataValue,
        },
      })
    }).catch(e => {
      updateClientState({
        ...clientState,
        config: {
          ...clientState.config, 
          alertMessage: e.response.data.responseMessage,
          alertSeverity: 'error',
          alertTitle: `Error - ${e.response.data.responseCode}`,
          showAlert: true,
        },
      })
    }).finally(() => {
      clearTimeout(backdropTimeout)
      setSubmittingViewMigratedDataDialog(false)
      setBackdrop(false)
      setShowViewMigratedDataDialog(false)
    })
  }

  return (
    <BorderedBox>
      <ContentBox>
        <Grid container direction="row" >
          <Grid alignItems="center" container item justifyContent="flex-start" md={10}>
            <Grid item>
              <Heading2 size={1}>TalentReef & Tracker I-9 integration</Heading2>
            </Grid>
          </Grid>
          <Grid alignItems="center" container item justifyContent="flex-end" md={2}>
            <Grid item>
              <SwitchFormControlLabel
                control={
                  <CCTSwitch
                    checked={client.enabled || false}
                    color="secondary"
                    data-testid="cct-enabled"
                    name="enabled"
                    onChange={handleChange}
                    size="medium"
                  />
                }
                label={`TRACKER ${client.enabled ? 'ENABLED' : 'DISABLED'}`}
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </Grid>
      </ContentBox>
      <PackageAndFeatures client={client} packagesArray={packagesArray} />
      <ContentBox borderSize={1}>
        <ClientAuditHistory  clientAuditHistory ="TrackerEnableHistory" trClientId={client.client_id}/>
      </ContentBox>
      <ContentBox borderSize={1}>
        <Grid container direction="row" >
          <Grid alignItems="center" container item justifyContent="flex-start" md={8}>
            <Grid item>
              <ClientAuditHistory clientAuditHistory ="TrackerMigrationHistory" trClientId={client.client_id} />
            </Grid>
          </Grid>
          <Grid alignItems="center" container item justifyContent="flex-end" md={4}>
            <Grid item>
              <SwitchFormControlLabel
                control={
                  <CCTSwitch
                    checked={client.viewMigratedData || false}
                    color="secondary"
                    data-testid="cct-view-migrated-data"
                    disabled={!client.enabled || client.trackerClientId === undefined}
                    name="viewMigratedData"
                    onChange={handleViewMigratedDataChange}
                    size="medium"
                  />
                }
                label="Access Legacy I-9 Records in Tracker"
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </Grid>
        
      </ContentBox>
      {showViewMigratedDataDialog && (
        <ViewMigratedDataConfirmDialog
          handleClose={() => setShowViewMigratedDataDialog(false)}
          handleSubmit={handleViewMigratedDataConfirm}
          isEnabling={pendingViewMigratedDataValue}
          isSubmitting={submittingViewMigratedDataDialog}
          open={showViewMigratedDataDialog}
        />
      )}
    </BorderedBox>
  )
}

export default ClientSettings
